import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Avatar,
  Button,
  Tag,
  Input,
  Spin,
  Radio,
  Space,
  App,
} from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { apiGetFollowUpReview } from "../../api/followUps";
import http from "../../services/http";

const { TextArea } = Input;

const QuestionnaireReview = () => {
  const { message } = App.useApp();
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [questionnaireData, setQuestionnaireData] = useState(null);
  const [doctorNotes, setDoctorNotes] = useState("");

  const fetchQuestionnaireDetails = async () => {
    try {
      setLoading(true);
      const response = await apiGetFollowUpReview(id);

      const data = response.data;
      setQuestionnaireData(data);
      setDoctorNotes(data?.doctorNotes || "");
    } catch (error) {
      console.error("Error fetching questionnaire:", error);
      message.error("Failed to load questionnaire details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuestionnaireDetails();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Handle submit review
  const handleSubmitReview = async () => {
    if (!doctorNotes.trim()) {
      message.warning("Please add doctor notes before submitting");
      return;
    }

    try {
      setSubmitting(true);
      await http.post(`/api/doctor/follow-up/review/${id}`, {
        doctorNotes: doctorNotes.trim(),
      });
      message.success("Questionnaire review submitted successfully");
      navigate("/follow-ups");
    } catch (error) {
      console.error("Error submitting review:", error);
      message.error("Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  // Get risk level info
  const getRiskInfo = (level) => {
    const riskMap = {
      1: { color: "gold", text: "Low Risk" },
      2: { color: "orange", text: "Medium Risk" },
      3: { color: "red", text: "Attention Required" },
    };
    return riskMap[level] || { color: "default", text: "Unknown" };
  };

  // Get selected option for a question
  const getSelectedOption = (options) => {
    return options?.find((opt) => opt.isSelected)?.option || null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (!questionnaireData) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <Card>
          <div className="text-center py-12">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              Questionnaire Not Found
            </h2>
            <p className="text-gray-500 mb-6">
              The questionnaire with ID {id} does not exist.
            </p>
            <Button type="primary" onClick={() => navigate("/follow-ups")}>
              Back to Follow-ups
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const riskInfo = getRiskInfo(questionnaireData.session?.riskLevel);
  const isReviewed = !!questionnaireData.session?.reviewedAt;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Back Button */}
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate("/follow-ups")}
        className="mb-4"
        type="text"
      >
        Back
      </Button>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Follow-ups</h1>
        <p className="text-gray-500 mt-1">
          Schedule patient follow-up consultations and review patient health
          check questionnaires
        </p>
      </div>

      {/* Patient Header Card */}
      <Card className="shadow-sm mb-6 rounded-lg">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <Avatar
              size={64}
              src={questionnaireData?.patient?.profileImage}
              style={{
                backgroundColor: questionnaireData?.patient?.profileImage
                  ? undefined
                  : "#8B5CF6",
              }}
            >
              {!questionnaireData?.patient?.profileImage &&
                questionnaireData?.patient?.fullName?.charAt(0)?.toUpperCase()}
            </Avatar>

            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {questionnaireData?.patient?.fullName}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Weight Loss Consultation
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {questionnaireData?.patient?.displayId}
              </p>
            </div>
          </div>

          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Risk Level:</span>
              <Tag color={riskInfo.color} className="rounded-full px-3 py-1">
                {riskInfo.text}
              </Tag>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Status:</span>
              <Tag color="green" className="rounded-full px-3 py-1">
                Active
              </Tag>
            </div>
          </div>
        </div>

        {/* Submitted Date */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <p className="text-sm text-gray-600">
            Submitted:{" "}
            <span className="font-medium text-gray-900">
              {dayjs(questionnaireData?.session?.submittedAt * 1000).format(
                "MMMM DD, YYYY - h:mm A"
              )}
            </span>
          </p>
        </div>
      </Card>

      {/* Dynamic Questions */}
      <div className="space-y-6">
        {questionnaireData?.questions?.map((question, index) => {
          const selectedOption = getSelectedOption(question.options);

          return (
            <Card key={question.id} className="shadow-sm rounded-lg">
              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-4">
                  {question.order}. {question.question}
                </h3>
                <Radio.Group value={selectedOption} disabled className="w-full">
                  <Space direction="vertical" className="w-full" size="middle">
                    {question.options?.map((option) => (
                      <Radio
                        key={option.id}
                        value={option.option}
                        className={`w-full p-4 border rounded-lg ${
                          option.isSelected
                            ? "bg-purple-50 border-purple-600"
                            : "bg-white"
                        }`}
                      >
                        <span
                          className={
                            option.isSelected
                              ? "text-purple-700 font-medium"
                              : "text-gray-700"
                          }
                        >
                          {option.option}
                        </span>
                      </Radio>
                    ))}
                  </Space>
                </Radio.Group>
              </div>
            </Card>
          );
        })}

        {/* Action Buttons */}
        {!isReviewed && (
          <div className="flex justify-end gap-3">
            <Button
              size="large"
              onClick={() => navigate("/follow-ups")}
              className="px-8"
            >
              Cancel
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={handleSubmitReview}
              loading={submitting}
              className="bg-purple-600 hover:bg-purple-700 border-purple-600 px-8"
            >
              Submit Review
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionnaireReview;
